import { gql } from '@apollo/client';

export const SIGN_UP = `
  mutation SignUp($input: SignUpInput!) {
    signUp(input: $input) {
      success
      message
      data {
        userSub
        accessToken
        refreshToken
        idToken
        expiresIn
      }
      error
    }
  }
`;

export const SIGN_IN = `
  mutation SignIn($input: SignInInput!) {
    signIn(input: $input) {
      success
      message
      data {
        accessToken
        refreshToken
        idToken
        expiresIn
      }
      error
    }
  }
`;

export const CONFIRM_EMAIL = `
  mutation ConfirmEmail($input: ConfirmEmailInput!) {
    confirmEmail(input: $input) {
      success
      message
      error
    }
  }
`;

export const COMPLETE_OAUTH_REGISTRATION = `
  mutation CompleteOAuthRegistration($input: CompleteOAuthRegistrationInput!) {
    completeOAuthRegistration(input: $input) {
      success
      message
      error
      data {
        userSub
      }
    }
  }
`;

export const UPDATE_USER = `
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      userId
      email
      name
      userType
      languagePreference
      profileImage
      bio
      website
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER_LANGUAGE = gql`
  mutation UpdateUserLanguage($input: UpdateUserInput!) {
    updateUser(input: $input) {
      userId
      languagePreference
      updatedAt
    }
  }
`;

// Host Profile Mutations
export const CREATE_HOST_PROFILE = `
  mutation CreateHostProfile($input: CreateHostProfileInput!) {
    createHostProfile(input: $input) {
      success
      message
      error
      data {
        hostId
        userId
        companyName
        contactEmail
        contactName
        contactPhone
        industry
        businessType
        companySize
        website
        address
        city
        country
        description
        establishedYear
        businessRegistrationNumber
        preferredCampaignTypes
        budgetRange
        isVerified
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_HOST_PROFILE = `
  mutation UpdateHostProfile($input: UpdateHostProfileInput!) {
    updateHostProfile(input: $input) {
      success
      message
      error
      data {
        hostId
        userId
        companyName
        contactEmail
        contactName
        contactPhone
        industry
        businessType
        companySize
        website
        address
        city
        country
        description
        establishedYear
        businessRegistrationNumber
        preferredCampaignTypes
        budgetRange
        isVerified
        latitude
        longitude
        createdAt
        updatedAt
      }
    }
  }
`;

export const CREATE_INFLUENCER = `
  mutation CreateInfluencer($input: CreateInfluencerInput!) {
    createInfluencer(input: $input) {
      success
      message
      error
      data {
        influencerId
        userId
        name
        email
        mainCategory
        categories
        socialMediaHandles {
          instagram { account followerCount }
          youtube { account followerCount }
          tiktok { account followerCount }
          twitter { account followerCount }
          naverblog { account followerCount }
        }
        engagementRate
        rating
        ratePerPost
        location
        languages
        profileImage
        portfolio
        bio
        isVerified
        availableFrom
        availableTo
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_INFLUENCER = `
  mutation UpdateInfluencer($input: UpdateInfluencerInput!) {
    updateInfluencer(input: $input) {
      success
      message
      error
      data {
        influencerId
        userId
        name
        email
        mainCategory
        categories
        activityType
        equipment
        maritalStatus
        hasChildren
        bankAccountType
        bankName
        bankAccountHolder
        bankAccountNumber
        socialMediaHandles {
          instagram { account followerCount }
          youtube { account followerCount }
          tiktok { account followerCount }
          twitter { account followerCount }
          naverblog { account followerCount }
        }
        profileImage
        bio
        isVerified
        availableFrom
        availableTo
        createdAt
        updatedAt
      }
    }
  }
`;

// Real-time messaging mutations
export const UPDATE_TYPING_STATUS = gql`
  mutation UpdateTypingStatus($input: TypingStatusInput!) {
    updateTypingStatus(input: $input) {
      userId
      conversationId
      isTyping
      timestamp
    }
  }
`;

export const UPDATE_PRESENCE = gql`
  mutation UpdatePresence($input: UserPresenceInput!) {
    updatePresence(input: $input) {
      userId
      conversationId
      isOnline
      lastSeen
      timestamp
    }
  }
`;
export const CREATE_CAMPAIGN = `
  mutation CreateCampaign($input: CreateCampaignInput!) {
    createCampaign(input: $input) {
      campaignId
      hostId
      createdBy
      status
      visibility
      isVerified
      createdAt
      updatedAt
      advertiserName
      brandName
      contactPersonName
      representativeImages {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      campaignName
      campaignType
      campaignCategory
      contentTypes
      recruitmentPeriod {
        startDate
        endDate
        alwaysRecruiting
      }
      numberOfRecruits
      announcementDate
      productName
      introductionPageUrl
      providedValueDescription
      provisionMethod
      publishingMode
      selectedChannels
      publishingConditions {
        instagram {
          collaboratorId
          conditions { item quantity }
        }
        blog {
          insertLink
          conditions { item quantity }
        }
        youtube {
          insertLink
          conditions { item quantity }
        }
      }
      productIntroduction
      contentWritingRequirements
      additionalNotes
      recommendedKeywords
      attachments {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      remarks
    }
  }
`;

export const UPDATE_CAMPAIGN = `
  mutation UpdateCampaign($input: UpdateCampaignInput!) {
    updateCampaign(input: $input) {
      campaignId
      hostId
      createdBy
      status
      visibility
      isVerified
      createdAt
      updatedAt
      advertiserName
      brandName
      contactPersonName
      clientBrandId
      clientBrandName
      representativeImages {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      campaignName
      campaignType
      campaignCategory
      contentTypes
      recruitmentPeriod {
        startDate
        endDate
        alwaysRecruiting
      }
      numberOfRecruits
      announcementDate
      productName
      introductionPageUrl
      providedValueDescription
      provisionMethod
      publishingMode
      selectedChannels
      publishingConditions {
        instagram {
          collaboratorId
          conditions { item quantity }
        }
        blog {
          insertLink
          conditions { item quantity }
        }
        youtube {
          insertLink
          conditions { item quantity }
        }
      }
      productIntroduction
      contentWritingRequirements
      additionalNotes
      recommendedKeywords
      attachments {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      remarks
    }
  }
`;

export const CREATE_MATCH = `
  mutation CreateMatch($input: CreateMatchInput!) {
    createMatch(input: $input) {
      matchId
      campaignId
      influencerId
      userId
      status
      proposedRate
      coverLetter
      portfolioItems
      estimatedDeliveryDays
      whyGoodFit
      previousWorkExamples
      socialMediaStats {
        totalReach
        avgEngagementRate
        topPerformingPost
      }
      matchScore
      selectedMissions
      visitInfo {
        startDate
        endDate
        adults
        children
        infants
      }
      campaignConsents {
        portrait
        campaignTerms
        privacy
        consentedAt
      }
      appliedAt
      respondedAt
      completedAt
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_MATCH = `
  mutation UpdateMatch($input: UpdateMatchInput!) {
    updateMatch(input: $input) {
      matchId
      status
      visitStatus
      contentSubmissions {
        seq
        channel
        item
        contentLink
        submittedAt
        status
      }
      respondedAt
      completedAt
      updatedAt
    }
  }
`;

export const SHORTLIST_MATCH = `
  mutation ShortlistMatch($matchId: ID!) {
    shortlistMatch(matchId: $matchId) {
      matchId
      status
      updatedAt
    }
  }
`;

export const REQUEST_MATCH = `
  mutation RequestMatch($matchId: ID!) {
    requestMatch(matchId: $matchId) {
      matchId
      status
      updatedAt
    }
  }
`;

export const ACCEPT_MATCH = `
  mutation AcceptMatch($matchId: ID!) {
    acceptMatch(matchId: $matchId) {
      matchId
      status
      respondedAt
      updatedAt
    }
  }
`;

export const REJECT_MATCH = `
  mutation RejectMatch($matchId: ID!, $rejectionReason: String!) {
    rejectMatch(matchId: $matchId, rejectionReason: $rejectionReason) {
      matchId
      status
      rejectionReason
      respondedAt
      updatedAt
    }
  }
`;

export const CANCEL_MATCH = `
  mutation CancelMatch($matchId: ID!, $cancellationReason: String) {
    cancelMatch(matchId: $matchId, cancellationReason: $cancellationReason) {
      matchId
      status
      updatedAt
    }
  }
`;

export const COMPLETE_MATCH = `
  mutation CompleteMatch($matchId: ID!) {
    completeMatch(matchId: $matchId) {
      matchId
      status
      completedAt
      updatedAt
    }
  }
`;

export const BULK_UPDATE_MATCHES = `
  mutation BulkUpdateMatches($matchIds: [ID!]!, $status: MatchStatus!, $reason: String) {
    bulkUpdateMatches(matchIds: $matchIds, status: $status, reason: $reason) {
      matchId
      status
      respondedAt
      updatedAt
    }
  }
`;

export const SEND_PHONE_VERIFICATION = `
  mutation SendPhoneVerification($input: SendPhoneVerificationInput!) {
    sendPhoneVerification(input: $input) {
      success
      message
      error
    }
  }
`;

export const CONFIRM_PHONE_VERIFICATION = `
  mutation ConfirmPhoneVerification($input: ConfirmPhoneVerificationInput!) {
    confirmPhoneVerification(input: $input) {
      success
      message
      error
    }
  }
`;

export const SEND_EMAIL_VERIFICATION = `
  mutation SendEmailVerification($input: SendEmailVerificationInput!) {
    sendEmailVerification(input: $input) {
      success
      message
      error
    }
  }
`;

export const CONFIRM_EMAIL_VERIFICATION = `
  mutation ConfirmEmailVerification($input: ConfirmEmailVerificationInput!) {
    confirmEmailVerification(input: $input) {
      success
      message
      error
    }
  }
`;

export const REQUEST_WITHDRAWAL = `
  mutation RequestWithdrawal($input: RequestWithdrawalInput!) {
    requestWithdrawal(input: $input) {
      success
      message
      error
      data {
        withdrawalId
        userId
        amount
        bankName
        bankAccountHolder
        bankAccountNumber
        status
        requestDate
        expectedDate
        completedDate
        createdAt
      }
    }
  }
`;


// Messages Mutations - Phase 3
export const SEND_MESSAGE = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      messageId
      conversationId
      senderId
      receiverId
      messageType
      content
      attachments {
        fileId
        fileName
        fileUrl
        fileType
      }
      readStatus {
        isRead
        readAt
      }
      campaignId
      matchId
      createdAt
      updatedAt
    }
  }
`;

export const MARK_MESSAGE_READ = gql`
  mutation MarkMessageRead($messageId: ID!) {
    markMessageRead(messageId: $messageId) {
      messageId
      readStatus {
        isRead
        readAt
      }
      updatedAt
    }
  }
`;

// Notifications Mutations - Phase 3
export const CREATE_NOTIFICATION = gql`
  mutation CreateNotification($input: CreateNotificationInput!) {
    createNotification(input: $input) {
      notificationId
      userId
      type
      title
      message
      relatedEntityType
      relatedEntityId
      actionUrl
      isRead
      priority
      createdAt
      updatedAt
    }
  }
`;

export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($notificationId: ID!) {
    markNotificationRead(notificationId: $notificationId) {
      notificationId
      isRead
      readAt
      updatedAt
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      updatedCount
    }
  }
`;

export const CREATE_CLIENT_BRAND = `
  mutation CreateClientBrand($input: CreateClientBrandInput!) {
    createClientBrand(input: $input) {
      success
      message
      error
      data {
        brandId
        userId
        brandName
        industry
        country
        location
        latitude
        longitude
        website
        contactPerson
        contactEmail
        contactPhone
        campaignCount
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_CLIENT_BRAND = `
  mutation UpdateClientBrand($input: UpdateClientBrandInput!) {
    updateClientBrand(input: $input) {
      success
      message
      error
      data {
        brandId
        userId
        brandName
        industry
        country
        location
        latitude
        longitude
        website
        contactPerson
        contactEmail
        contactPhone
        campaignCount
        createdAt
        updatedAt
      }
    }
  }
`;

export const DELETE_CLIENT_BRAND = `
  mutation DeleteClientBrand($brandId: ID!) {
    deleteClientBrand(brandId: $brandId) {
      success
      message
      error
    }
  }
`;

export const CREATE_INQUIRY = `
  mutation CreateInquiry($input: CreateInquiryInput!) {
    createInquiry(input: $input) {
      success
      message
      error
      data {
        inquiryId
        userId
        title
        content
        status
        createdAt
      }
    }
  }
`;

export const DELETE_INQUIRY = `
  mutation DeleteInquiry($inquiryId: ID!) {
    deleteInquiry(inquiryId: $inquiryId) {
      success
      message
      error
    }
  }
`;

export const CREATE_INFLUENCER_REVIEW = `
  mutation CreateInfluencerReview($input: CreateInfluencerReviewInput!) {
    createInfluencerReview(input: $input) {
      success
      message
      error
      data {
        reviewId
        matchId
        campaignId
        influencerId
        communicationSpeed
        visitPunctuality
        collaborationAttitude
        submissionPunctuality
        contentQuality
        averageGrade
        comment
        createdAt
      }
    }
  }
`;

export const SET_USER_STATUS_ADMIN = `
  mutation SetUserStatusAdmin($input: SetUserStatusAdminInput!) {
    setUserStatusAdmin(input: $input) {
      success
      message
      user {
        userId
        status
      }
    }
  }
`;

export const RESET_USER_PASSWORD_ADMIN = `
  mutation ResetUserPasswordAdmin($input: ResetUserPasswordAdminInput!) {
    resetUserPasswordAdmin(input: $input) {
      success
      message
    }
  }
`;

// Document Templates Mutations
export const UPDATE_DOCUMENT_TEMPLATE = gql`
  mutation UpdateDocumentTemplate($docType: String!, $content: String!) {
    updateDocumentTemplate(docType: $docType, content: $content) {
      docType
      content
      createdAt
      updatedAt
      createdBy
      createdByName
      updatedBy
      updatedByName
    }
  }
`;
